'use client'

import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react'

import { type ProductSearchConfig } from '../config/ProductSearchConfig'

/**
 * this is a general context for a search page, it contains a page wide
 * config, and logic.
 */
type ProductSearchContextValue = {
  productSearchConfig: ProductSearchConfig
  skipProductDeepLinkParams?: boolean
}

const ProductSearchContext = createContext<ProductSearchContextValue | null>(
  null,
)

export const ProductSearchContextProvider: FC<
  PropsWithChildren<{
    productSearchConfig: ProductSearchConfig
    skipProductDeepLinkParams?: boolean
  }>
> = ({ children, productSearchConfig, skipProductDeepLinkParams = false }) => {
  const value = useMemo(
    (): ProductSearchContextValue => ({
      productSearchConfig,
      skipProductDeepLinkParams,
    }),
    [productSearchConfig, skipProductDeepLinkParams],
  )

  return (
    <ProductSearchContext.Provider value={value}>
      {children}
    </ProductSearchContext.Provider>
  )
}

export const useProductSearchContext = (): ProductSearchContextValue => {
  const context = useContext(ProductSearchContext)
  if (context === null) {
    throw new Error(
      'useProductSearchContext must be used within a ProductSearchContextProvider',
    )
  }

  return context
}
